import React, { useState, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Searchable single-select with full-text filter over options.
 *
 * props:
 *   value          string | ""
 *   onChange       (value) => void
 *   options        Array<{ value, label, hint?, disabled?, keywords? }>
 *   placeholder    string
 *   empty          string
 *   testid         string (root testid)
 */
export function SearchableSelect({
  value, onChange, options = [], placeholder = "Оберіть",
  empty = "Нічого не знайдено", testid, className = "", disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          data-testid={testid}
          className={`w-full justify-between font-normal ${!selected ? "text-slate-500" : ""} ${className}`}
        >
          {selected ? selected.label : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
        <Command
          filter={(value_, search) => {
            const opt = options.find((o) => o.value === value_);
            if (!opt) return 0;
            const haystack = (
              opt.label + " " + (opt.hint || "") + " " + (opt.keywords || [])
            ).toLowerCase();
            const needle = (search || "").toLowerCase().trim();
            if (!needle) return 1;
            return haystack.includes(needle) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Пошук…" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList className="max-h-72 overflow-y-auto overscroll-contain">
            <CommandEmpty>{empty}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                  onSelect={(v) => { onChange(v); setOpen(false); }}
                  className={opt.disabled ? "opacity-50" : ""}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === opt.value ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex-1">
                    <div>{opt.label}</div>
                    {opt.hint && <div className="text-xs text-slate-500">{opt.hint}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Normalize diopter input: accept "21" / "21.0" / "21.5" / "21,5" → "21,0" / "21,5".
 *  Only allow 0.5 step. Returns "" for empty / invalid.
 */
export function normalizeDiopter(raw) {
  if (raw === null || raw === undefined) return "";
  let s = String(raw).trim().replace(",", ".");
  if (!s) return "";
  // Allow leading + / -
  const m = s.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!m) return null; // invalid
  const sign = m[1] || "+";
  const int = parseInt(m[2], 10);
  const fracStr = m[3] || "0";
  const frac = parseInt(fracStr[0], 10); // first decimal
  if (![0, 5].includes(frac)) return null;
  return `${sign}${int},${frac}`;
}
